import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { get, post, patch } from '../lib/apiClient';
import { createEchoInstance } from '../lib/echo';
import { useAuth } from '../context/AuthContext';

export interface AppNotification {
  id: string;
  type: 'new_replies' | 'bounced_emails' | 'campaign_completed' | 'new_leads_added' | 'security_alerts';
  title: string;
  body: string;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

interface PaginatedNotificationsResponse {
  notifications: AppNotification[];
  unread_count: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------
export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

// ---------------------------------------------------------------------------
// Format ISO timestamp: "21st Apr 2026 3:05 PM"
// ---------------------------------------------------------------------------
function formatNotifDate(isoString: string): string {
  const d = new Date(isoString);
  const day = d.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? 'st'
    : day % 10 === 2 && day !== 12 ? 'nd'
    : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}${suffix} ${month} ${year} ${time}`;
}

// ---------------------------------------------------------------------------
// Fetch hook
// ---------------------------------------------------------------------------
export function useNotifications() {
  const { user, token, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.organization?.id ?? null;

  // -------------------------------------------------------------------------
  // Paginated notifications via infinite query
  // -------------------------------------------------------------------------
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<PaginatedNotificationsResponse>({
      queryKey: NOTIFICATIONS_QUERY_KEY,
      queryFn: async ({ pageParam }) => {
        const res = await get<PaginatedNotificationsResponse>(
          `/notifications?page=${pageParam}&per_page=20`
        );
        return res.data as PaginatedNotificationsResponse;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.has_more ? lastPage.page + 1 : undefined,
      enabled: isAuthenticated,
      staleTime: 30_000,
    });

  // -------------------------------------------------------------------------
  // Notification preferences (shared cache key with SettingsPage)
  // -------------------------------------------------------------------------
  const { data: prefsResponse } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => get<{ preferences: Record<string, boolean> }>('/settings/notifications'),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const notifPrefs: Record<string, boolean> = prefsResponse?.data?.preferences ?? {};

  // Use a ref so the Echo listener always reads the latest prefs without
  // needing prefs in its dep array (which would reconnect the socket on every toggle).
  const notifPrefsRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    notifPrefsRef.current = notifPrefs;
  }, [notifPrefs]);

  // -------------------------------------------------------------------------
  // Real-time: subscribe to org channel and listen for NotificationCreated
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!token || !orgId) return;

    try {
      const echo = createEchoInstance(token);
      if (!echo) return;

      const channelName = `org.${orgId}`;

      echo.private(channelName).listen('.NotificationCreated', (payload: AppNotification) => {
        // When prefs are loaded, respect the user's toggle; default to enabled while loading.
        const prefsLoaded = Object.keys(notifPrefsRef.current).length > 0;
        const isEnabled = prefsLoaded ? (notifPrefsRef.current[payload.type] ?? true) : true;

        if (isEnabled) {
          // Only invalidate (updating badge count) and show toast for enabled types.
          queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
          toast(payload.title, {
            description: `${payload.body} · ${formatNotifDate(payload.created_at)}`,
            duration: 5000,
          });
        }
      });

      return () => {
        // Leave channel; keep the Echo singleton alive to avoid full reconnect
        echo.leave(channelName);
      };
    } catch (err) {
      console.warn('[useNotifications] Pusher init failed:', err);
    }
  }, [token, orgId, queryClient]);

  // -------------------------------------------------------------------------
  // Flatten pages and filter by enabled preferences
  // -------------------------------------------------------------------------
  const allNotifications = data?.pages.flatMap(p => p.notifications) ?? [];
  const filteredNotifications = allNotifications.filter(
    (n) => notifPrefs[n.type] ?? true
  );

  // Use server's total unread count (from first page) — reflects ALL unread,
  // not just the items loaded so far.
  const unreadCount = data?.pages[0]?.unread_count ?? 0;

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------
  const markReadMutation = useMutation({
    mutationFn: (id: string) => patch(`/notifications/${id}/read`),
    onMutate: async (id: string) => {
      // Optimistic update across all loaded pages
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData(
        NOTIFICATIONS_QUERY_KEY,
        (old: InfiniteData<PaginatedNotificationsResponse> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page, idx) => ({
              ...page,
              notifications: page.notifications.map((n) =>
                n.id === id ? { ...n, read_at: new Date().toISOString() } : n
              ),
              unread_count: idx === 0 ? Math.max(0, page.unread_count - 1) : page.unread_count,
            })),
          };
        }
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, ctx.previous);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => post('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });

  return {
    notifications: filteredNotifications,
    unreadCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    markRead: (id: string) => markReadMutation.mutate(id),
    markAllRead: () => markAllReadMutation.mutate(),
  };
}
